import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import type { ChatStore, ChatSessionState, ComposerAlertState } from './types';
import type { Alert } from '@/modules/alerts/types';
import getApi from '@/services/GetApi';
import { buildConversationId } from '@/utils/chatConversation';
import { parsePlanAndAnswer } from '@/utils/aidaStreamParse';

const streamControllers = new Map<string, AbortController>();
const streamGenerations = new Map<string, number>();

type ChatApiErrorJson = {
  status?: number;
  error?: string;
  window?: string;
  limit?: number;
  reset_after_seconds?: number;
};

class ChatUserFacingError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ChatUserFacingError';
  }
}

/** Absolute reset time in the user's locale (avoids stale "in 9 seconds" copy). */
const formatLocalResetTime = (resetAfterSeconds: number): string => {
  const resetAt = new Date(Date.now() + Math.max(0, resetAfterSeconds) * 1000);
  const locale = typeof navigator !== 'undefined' && navigator.language ? navigator.language : undefined;
  return new Intl.DateTimeFormat(locale, {
    dateStyle: 'medium',
    timeStyle: 'medium',
  }).format(resetAt);
};

const ensureSentenceEnd = (text: string): string => {
  const t = text.trim();
  if (!t) return t;
  return /[.!?…]$/.test(t) ? t : `${t}.`;
};

const resolveChatFetchErrorMessage = async (response: Response): Promise<string> => {
  let raw = '';
  try {
    raw = await response.text();
  } catch {
    return response.status === 504
      ? 'The request is taking longer than expected. Please try a simpler question or try again later.'
      : response.statusText || 'Sorry, something went wrong. Please try again.';
  }

  const trimmed = raw.trim();
  if (trimmed) {
    try {
      const data = JSON.parse(trimmed) as ChatApiErrorJson;
      if (typeof data.error === 'string' && data.error.trim()) {
        let msg = ensureSentenceEnd(data.error.trim());
        if (
          response.status === 429 &&
          typeof data.reset_after_seconds === 'number' &&
          Number.isFinite(data.reset_after_seconds)
        ) {
          msg += ` The limit resets at ${formatLocalResetTime(data.reset_after_seconds)} (your local time). [Learn more about request limits](/Tools?section=assistant).`;
        }
        return msg;
      }
    } catch {
      if (trimmed.length <= 600) return trimmed;
    }
  }

  if (response.status === 504) {
    return 'The request is taking longer than expected. Please try a simpler question or try again later.';
  }

  return (
    response.statusText ||
    (response.status
      ? `Request failed (${response.status}).`
      : 'Sorry, something went wrong. Please try again.')
  );
};

const getDefaultSession = (): ChatSessionState => ({
  messages: [],
  isStreaming: false,
  botStatus: null,
  isChatActive: false,
});

const notifyAnswerReady = (question: string) => {
  if (typeof window === 'undefined' || typeof Notification === 'undefined') return;
  if (Notification.permission !== 'granted') return;

  if (document.visibilityState === 'visible') return;

  const trimmedQuestion = question.trim();
  const body =
    trimmedQuestion.length > 0
      ? `Your answer to "${trimmedQuestion.slice(0, 60)}${trimmedQuestion.length > 60 ? '…' : ''}" is ready.`
      : 'Your AIDA answer is ready. Click to open AIDA.';

  try {
    const notification = new Notification('AIDA answer ready', {
      body,
      tag: 'aida-answer-ready',
      renotify: true,
      icon: '/static/images/eyesAida.svg',
    });

    notification.onclick = () => {
      window.focus();
      window.location.assign('/Assistant');
      notification.close();
    };
  } catch (error) {
    console.warn('AIDA notification could not be displayed', error);
  }
};

const emitInAppAnswerReadyAlert = (question: string, chatId: string) => {
  if (typeof window === 'undefined') return;

  const trimmedQuestion = question.trim();
  const message =
    trimmedQuestion.length > 0
      ? trimmedQuestion.length > 60
        ? `${trimmedQuestion.slice(0, 57)}...`
        : trimmedQuestion
      : 'Your AIDA answer is ready.';

  const alertPayload: Alert = {
    id: `aida-ready-${Date.now()}`,
    type: 'aida',
    title: 'AIDA answer ready',
    message,
    target: 'all',
    priority: 1,
    buttons: [
      {
        text: 'Go to chat',
        action: 'link',
        url: `/Assistant?chatId=${encodeURIComponent(chatId)}`, // ← aquí
        style: 'primary',
        dismissOnClick: true,
      },
    ],
  };

  window.dispatchEvent(new CustomEvent<Alert>('enqueueAlert', { detail: alertPayload }));
};

const isInAidaView = (): boolean =>
  typeof window !== 'undefined' && window.location.pathname.startsWith('/Assistant');

export const useChatStore = create<ChatStore>()(
  persist(
    (set, get) => ({
      chatHistory: [],
      chatSessions: {},
      aidaNotificationsEnabled: false,
      composerAlert: null,

      setChatHistory: history => {
        set(state => ({
          chatHistory: typeof history === 'function' ? history(state.chatHistory) : history,
        }));
      },

      setAidaNotificationsEnabled: enabled => {
        set({ aidaNotificationsEnabled: enabled });
      },

      clearChatHistory: () => {
        set({ chatHistory: [] });
      },

      clearComposerAlert: () => {
        set({ composerAlert: null });
      },

      ensureSession: (chatId, initialBotMessage) => {
        set(state => {
          if (state.chatSessions[chatId]) return state;
          return {
            chatSessions: {
              ...state.chatSessions,
              [chatId]: { ...getDefaultSession(), messages: [initialBotMessage] },
            },
          };
        });
      },

      getSessionMessages: chatId => get().chatSessions[chatId]?.messages ?? [],

      addUserMessage: (chatId, message) => {
        set(state => {
          const session = state.chatSessions[chatId] ?? getDefaultSession();
          const updatedMessages = [...session.messages, message];
          return {
            chatHistory: updatedMessages,
            chatSessions: {
              ...state.chatSessions,
              [chatId]: { ...session, messages: updatedMessages, isChatActive: true },
            },
          };
        });
      },

      clearSession: (chatId, initialBotMessage) => {
        set(state => ({
          chatHistory: [initialBotMessage],
          chatSessions: {
            ...state.chatSessions,
            [chatId]: { ...getDefaultSession(), messages: [initialBotMessage] },
          },
          composerAlert: state.composerAlert?.chatId === chatId ? null : state.composerAlert,
        }));
      },

      deleteSession: chatId => {
        set(state => {
          const rest = { ...state.chatSessions };
          delete rest[chatId];
          const composerAlert = state.composerAlert?.chatId === chatId ? null : state.composerAlert;
          return { chatSessions: rest, composerAlert };
        });
      },

      renameSession: (chatId, title) => {
        set(state => {
          const session = state.chatSessions[chatId];
          if (!session) return state;
          return {
            chatSessions: {
              ...state.chatSessions,
              [chatId]: { ...session, customTitle: title.trim() || undefined },
            },
          };
        });
      },

      streamBotResponse: async ({ chatId, userMessage, userMessageId, user, authorization }) => {
        const currentTime = new Date();
        const controller = new AbortController();
        const generation = (streamGenerations.get(chatId) ?? 0) + 1;
        streamGenerations.set(chatId, generation);

        const previousController = streamControllers.get(chatId);
        if (previousController) previousController.abort();
        streamControllers.set(chatId, controller);

        const botMessageId = `bot-${currentTime.getTime()}`;
        let rawText = '';
        let didFinishSuccessfully = false;

        const applyStreamBuffer = () => {
          if (streamGenerations.get(chatId) !== generation) return;
          const { reasoning, reasoningSteps, answer } = parsePlanAndAnswer(rawText);
          set(state => {
            const session = state.chatSessions[chatId] ?? getDefaultSession();
            const updatedMessages = session.messages.map(msg =>
              msg.id === botMessageId
                ? {
                    ...msg,
                    message: '',
                    streamTarget: answer,
                    reasoning,
                    reasoningSteps,
                  }
                : msg,
            );
            return {
              chatHistory: updatedMessages,
              chatSessions: {
                ...state.chatSessions,
                [chatId]: {
                  ...session,
                  messages: updatedMessages,
                  // Keep thinking loader until answer text starts arriving (not on plan alone).
                  botStatus: answer.length > 0 ? null : session.botStatus,
                },
              },
            };
          });
        };

        set(state => {
          const session = state.chatSessions[chatId] ?? getDefaultSession();
          return {
            chatSessions: {
              ...state.chatSessions,
              [chatId]: {
                ...session,
                messages: [
                  ...session.messages,
                  { id: botMessageId, sender: 'bot', message: '', timestamp: new Date() },
                ],
                isStreaming: true,
                streamBufferClosed: false,
                botStatus: 'thinking',
                isChatActive: true,
              },
            },
          };
        });

        try {
          const userApiKey = await getApi(user?.sub);
          const apiKey = userApiKey?.payload?.api_key;
          const conversationId = apiKey ? buildConversationId(apiKey, chatId) : chatId;
          const headers = {
            Authorization: `Bearer ${authorization}`,
            'x-conversation-id': conversationId,
            ...(apiKey ? { 'x-api-key': apiKey } : {}),
          };
          const url = `${process.env.REACT_APP_URL_CHAT_API}get?msg=${encodeURIComponent(userMessage)}`;

          const response = await fetch(url, {
            method: 'GET',
            headers,
            credentials: 'include',
            signal: controller.signal,
            keepalive: true,
          });

          if (!response.ok) {
            const apiMessage = await resolveChatFetchErrorMessage(response);
            throw new ChatUserFacingError(apiMessage);
          }

          if (!response.body) throw new Error('No response body received');

          const reader = response.body.getReader();
          const decoder = new TextDecoder();
          let done = false;

          while (!done) {
            if (controller.signal.aborted) break;
            const { value, done: chunkDone } = await reader.read();
            done = chunkDone;
            if (value) {
              rawText += decoder.decode(value, { stream: true });
              applyStreamBuffer();
            }
          }

          if (!controller.signal.aborted && streamGenerations.get(chatId) === generation) {
            applyStreamBuffer();

            if (get().aidaNotificationsEnabled) {
              const notifSession = get().chatSessions[chatId];
              const lastUser = [...(notifSession?.messages ?? [])].reverse().find(m => m.sender === 'user');
              if (lastUser && typeof lastUser.message === 'string') {
                notifyAnswerReady(lastUser.message);
                if (!isInAidaView()) {
                  emitInAppAnswerReadyAlert(lastUser.message, chatId);
                }
              }
            }

            set(state => {
              if (streamGenerations.get(chatId) !== generation) return state;
              const session = state.chatSessions[chatId] ?? getDefaultSession();
              return {
                chatSessions: {
                  ...state.chatSessions,
                  [chatId]: { ...session, streamBufferClosed: true },
                },
              };
            });
            didFinishSuccessfully = true;
          }
        } catch (error: unknown) {
          const isActiveController = streamControllers.get(chatId) === controller;
          if (!isActiveController || controller.signal.aborted) {
            return;
          }

          const fallback = 'Sorry, there was an error processing your request. Please try again.';
          const errorMessage =
            error instanceof ChatUserFacingError
              ? error.message
              : error instanceof Error && error.message.includes('taking longer than expected')
                ? error.message
                : fallback;

          set(state => {
            const session = state.chatSessions[chatId] ?? getDefaultSession();
            const updatedMessages = session.messages.filter(
              m => m.id !== userMessageId && m.id !== botMessageId,
            );
            const alert: ComposerAlertState = {
              chatId,
              message: errorMessage,
              restoreDraft: userMessage,
            };
            return {
              composerAlert: alert,
              chatHistory: updatedMessages,
              chatSessions: {
                ...state.chatSessions,
                [chatId]: { ...session, messages: updatedMessages, isStreaming: false, botStatus: null },
              },
            };
          });
        } finally {
          const isActiveStream = streamControllers.get(chatId) === controller;
          if (isActiveStream) streamControllers.delete(chatId);

          if (isActiveStream && !didFinishSuccessfully) {
            set(state => {
              const session = state.chatSessions[chatId] ?? getDefaultSession();
              return {
                chatSessions: {
                  ...state.chatSessions,
                  [chatId]: {
                    ...session,
                    isStreaming: false,
                    streamBufferClosed: false,
                    botStatus: null,
                  },
                },
              };
            });
          }
        }
      },

      finalizeStreamMessage: chatId => {
        const sessionBefore = get().chatSessions[chatId];
        if (!sessionBefore?.isStreaming) return;

        set(state => {
          const session = state.chatSessions[chatId];
          if (!session?.isStreaming) return state;

          const lastBotIndex = session.messages.findLastIndex(msg => msg.sender === 'bot');
          const updatedMessages = session.messages.map((msg, index) => {
            if (index !== lastBotIndex || msg.sender !== 'bot') return msg;
            if (msg.streamTarget === undefined) return msg;
            return {
              ...msg,
              message: msg.streamTarget,
              streamTarget: undefined,
            };
          });

          return {
            chatHistory: updatedMessages,
            chatSessions: {
              ...state.chatSessions,
              [chatId]: {
                ...session,
                messages: updatedMessages,
                isStreaming: false,
                streamBufferClosed: false,
                botStatus: null,
              },
            },
          };
        });
      },

      syncBotInterruptedDisplay: (chatId, messageId, displayText) => {
        set(state => {
          const session = state.chatSessions[chatId];
          if (!session) return state;

          const updatedMessages = session.messages.map(msg => {
            if (msg.id !== messageId) return msg;
            const partial = displayText.trim();
            return {
              ...msg,
              message: partial,
              streamTarget: undefined,
            };
          });

          return {
            chatHistory: updatedMessages,
            chatSessions: {
              ...state.chatSessions,
              [chatId]: { ...session, messages: updatedMessages },
            },
          };
        });
      },

      stopStreaming: (chatId, withInterruption) => {
        const controller = streamControllers.get(chatId);
        if (controller) {
          controller.abort();
          streamControllers.delete(chatId);
        }
        set(state => {
          const session = state.chatSessions[chatId];
          if (!session) return state;

          const updatedMessages = withInterruption
            ? session.messages.map((msg, i) => {
                const isLastBotMessage = i === session.messages.length - 1 && msg.sender === 'bot';
                if (isLastBotMessage) {
                  return { ...msg, interrupted: true };
                }
                return msg;
              })
            : session.messages;

          return {
            chatSessions: {
              ...state.chatSessions,
              [chatId]: {
                ...session,
                messages: updatedMessages,
                isStreaming: false,
                streamBufferClosed: false,
                botStatus: null,
              },
            },
          };
        });
      },
    }),
    {
      name: 'chat-storage',
      storage: createJSONStorage(() => sessionStorage),
      partialize: state => ({
        chatHistory: state.chatHistory,
        chatSessions: state.chatSessions,
        aidaNotificationsEnabled: state.aidaNotificationsEnabled,
      }),
    },
  ),
);
